import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { EventType, type AuthenticationResult } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { QueryClientProvider } from '@tanstack/react-query';

import { queryClient } from './api/queryClient.js';
import { msalInstance } from './auth/msalConfig.js';
import { App } from './App.js';
import { PublicProjectPortalPage } from './features/project-portal/PublicProjectPortalPage.js';
import { PublicTicketPortalPage } from './features/ticket-portal/PublicTicketPortalPage.js';
import { PublicForecastPage } from './features/forecast/PublicForecastPage.js';
import { PublicWelcomePage } from './features/onboarding-portal/PublicWelcomePage.js';
import { PublicSalesRepPage } from './features/onboarding-portal/PublicSalesRepPage.js';
import { PublicPmoPage } from './features/onboarding-portal/PublicPmoPage.js';
import { PublicAgreementUsagePage } from './features/agreement-portal/PublicAgreementUsagePage.js';
import { PublicTimesheetApprovalPage } from './features/timesheet-approval/PublicTimesheetApprovalPage.js';
import { PublicQuotePage } from './features/quotes/PublicQuotePage.js';
import { PublicQuoteSowPage } from './features/quotes/PublicQuoteSowPage.js';
import { SetupNotice } from './components/SetupNotice.js';
import { PublicMaintenanceNotice } from './components/layout/PublicMaintenanceNotice.js';
import { isAuthConfigured } from './config/env.js';
import { ThemeProvider } from './theme/ThemeProvider.js';
import { QuickAccessProvider } from './features/quick-access/QuickAccessProvider.js';
import './index.css';

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root container #root not found');
}

const root = createRoot(container);

/**
 * The public portals (/p/:token for projects, /t/:token for tickets) are unauthenticated —
 * they must render WITHOUT MSAL/Entra so external visitors are never asked to sign in.
 * Detect them before the normal auth bootstrap and render a minimal, auth-free tree.
 */
function renderPublicPortal(): void {
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <PublicMaintenanceNotice />
          <Routes>
            <Route path="/p/:token" element={<PublicProjectPortalPage />} />
            <Route path="/t/:token" element={<PublicTicketPortalPage />} />
            <Route path="/f/:token" element={<PublicForecastPage />} />
            <Route path="/w/:token" element={<PublicWelcomePage />} />
            <Route path="/sr/:token" element={<PublicSalesRepPage />} />
            <Route path="/pm/:token" element={<PublicPmoPage />} />
            <Route path="/a/:token" element={<PublicAgreementUsagePage />} />
            <Route path="/ta/:token" element={<PublicTimesheetApprovalPage />} />
            <Route path="/q/:token" element={<PublicQuotePage />} />
            <Route path="/sow/:token" element={<PublicQuoteSowPage />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}

async function bootstrap(): Promise<void> {
  if (
    window.location.pathname.startsWith('/p/') ||
    window.location.pathname.startsWith('/t/') ||
    window.location.pathname.startsWith('/f/') ||
    window.location.pathname.startsWith('/w/') ||
    window.location.pathname.startsWith('/sr/') ||
    window.location.pathname.startsWith('/pm/') ||
    window.location.pathname.startsWith('/a/') ||
    window.location.pathname.startsWith('/ta/') ||
    window.location.pathname.startsWith('/q/') ||
    window.location.pathname.startsWith('/sow/')
  ) {
    renderPublicPortal();
    return;
  }

  if (!isAuthConfigured || !msalInstance) {
    root.render(
      <StrictMode>
        <SetupNotice />
      </StrictMode>,
    );
    return;
  }

  await msalInstance.initialize();

  const instance = msalInstance;
  const accounts = instance.getAllAccounts();
  if (accounts.length > 0 && !instance.getActiveAccount()) {
    instance.setActiveAccount(accounts[0] ?? null);
  }

  instance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const result = event.payload as AuthenticationResult;
      instance.setActiveAccount(result.account);
    }
  });

  root.render(
    <StrictMode>
      <MsalProvider instance={instance}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <QuickAccessProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </QuickAccessProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </MsalProvider>
    </StrictMode>,
  );
}

void bootstrap();
